import { Link } from "react-router-dom";
import { Check, ArrowRight, Zap } from "lucide-react";
import { AirbnbLogo, BookingLogo, VrboLogo } from "./BrandLogos";

const ChannelManager = () => {
  return (
    <section className="py-24 lg:py-32 bg-cream" data-testid="channel-manager-section">
      <div className="container-tm grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        {/* Left: explainer */}
        <div className="lg:col-span-7">
          <span className="overline">Notre service</span>
          <h2 className="mt-5 font-serif font-light text-ink text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            Un channel manager
            <span className="italic text-gold"> tout-en-un</span>.
          </h2>
          <p className="mt-7 font-sans text-base md:text-lg text-ink/70 leading-relaxed max-w-xl">
            Avec Terre et Mer Conciergerie, oubliez les contraintes liées à la publication et au suivi de votre annonce sur les différentes plateformes. Notre channel manager se charge de diffuser et de synchroniser automatiquement votre logement en temps réel sur nos partenaires.
          </p>

          {/* Synced platforms */}
          <div className="mt-10 bg-white rounded-3xl p-7 border border-ink/5 shadow-soft">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-9 h-9 rounded-xl bg-sage/10 flex items-center justify-center">
                <Zap size={16} strokeWidth={2} className="text-sage" />
              </span>
              <span className="text-[11px] uppercase tracking-[0.25em] font-semibold text-sage">
                Synchronisation temps réel
              </span>
            </div>
            <div className="grid grid-cols-3 gap-4 items-center">
              <div className="flex items-center justify-center bg-cream/40 rounded-xl py-5 px-4">
                <AirbnbLogo className="h-7 w-auto" />
              </div>
              <div className="flex items-center justify-center bg-cream/40 rounded-xl py-5 px-4">
                <BookingLogo className="h-6 w-auto" />
              </div>
              <div className="flex items-center justify-center bg-cream/40 rounded-xl py-5 px-4">
                <VrboLogo className="h-7 w-auto" />
              </div>
            </div>
          </div>

          {/* Service explainer */}
          <div className="mt-8 space-y-3">
            <Bullet>
              <strong className="text-ink">Notre commission : 15% TTC</strong> par réservation. Frais de ménage à la charge des voyageurs.
            </Bullet>
            <Bullet>
              Vous encaissez l'intégralité des montants par Airbnb. Nous vous envoyons une facture de prestation de services tous les 30 jours.
            </Bullet>
            <Bullet>
              <strong className="text-ink">Linge non inclus</strong> dans la formule : par défaut les voyageurs apportent leur propre linge de lit et de toilette. S'ils préfèrent, ils peuvent souscrire l'<strong className="text-ink">option linge premium</strong> directement auprès de nous (facturée à eux, neutre pour vous) : draps-housses jusqu'à 160×200 cm, housses de couette jusqu'à 220×240 cm, taies d'oreiller et serviettes livrés et installés.
            </Bullet>
          </div>
        </div>

        {/* Right: commission card */}
        <div className="lg:col-span-5 lg:sticky lg:top-32">
          <div className="bg-ink text-white rounded-3xl p-8 md:p-10 grain-overlay relative overflow-hidden shadow-lift">
            <div className="absolute -top-20 -right-20 w-[300px] h-[300px] bg-gold/25 rounded-full blur-[120px] pointer-events-none" />

            <div className="relative">
              <div className="flex flex-col gap-1 mb-6">
                <span className="text-[10px] uppercase tracking-[0.28em] font-semibold text-gold">
                  Aucune avance de frais
                </span>
                <span className="text-[10px] uppercase tracking-[0.28em] font-semibold text-gold">
                  Vos revenus Airbnb en direct
                </span>
              </div>

              <div className="flex items-baseline gap-2">
                <span className="font-serif text-7xl md:text-8xl text-white leading-none">
                  15<span className="text-gold">%</span>
                </span>
                <span className="font-sans text-sm uppercase tracking-[0.2em] text-white/60 ml-2">
                  TTC
                </span>
              </div>
              <p className="mt-3 font-sans text-sm text-white/70 leading-relaxed">
                de commission<br />
                <span className="text-white/50">(forfait ménage à la charge du voyageur)</span>
              </p>

              <Link
                to="/contact"
                state={{ scrollTo: "simulator" }}
                className="btn-accent mt-8 w-full group"
                data-testid="channel-cta-calculate"
              >
                Calculez vos revenus
                <ArrowRight size={14} strokeWidth={2} className="ml-3 group-hover:translate-x-1 transition-transform" />
              </Link>

              <div className="mt-7 pt-6 border-t border-white/10 space-y-3">
                <DarkBullet>Photos professionnelles incluses</DarkBullet>
                <DarkBullet>Ajustement quotidien des prix</DarkBullet>
                <DarkBullet>Accueil voyageurs 24h/24, 7j/7 (permanence de nuit)</DarkBullet>
                <DarkBullet>Suivi propriétaire mensuel</DarkBullet>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Bullet = ({ children }) => (
  <div className="flex items-start gap-3">
    <span className="w-6 h-6 rounded-full bg-gold/15 flex items-center justify-center shrink-0 mt-0.5">
      <Check size={13} strokeWidth={2.5} className="text-gold" />
    </span>
    <p className="font-sans text-sm md:text-base text-ink/70 leading-relaxed">{children}</p>
  </div>
);

const DarkBullet = ({ children }) => (
  <div className="flex items-center gap-3 text-sm font-sans text-white/80">
    <Check size={15} strokeWidth={2} className="text-gold shrink-0" />
    <span>{children}</span>
  </div>
);

export default ChannelManager;
